<template>
  <div class="dashboard p-6 space-y-6">

    <!-- 顶部欢迎 + 最近登录时间 -->
    <div class="welcome-bar">
      <div class="welcome-text glass-card">
        <p class="welcome-title">欢迎回来，{{ username }}</p>
      </div>
      <div class="last-login glass-card">
        上次登录时间：{{ lastLoginTime }}
      </div>
    </div>

    <!-- 第一排统计卡片：植物 / 用户 / 商品 -->
    <div class="stats-row">
      <div class="stat-block glass-card">
        <img src="https://img.icons8.com/?size=100&id=1m9yTM1QHHFQ&format=png&color=000000" class="icon-img animate-breath" />
        <p class="stat-label">植物数量</p>
        <p class="stat-number gradient-number">{{ animated.plantCount }}</p>
      </div>

      <div class="stat-block glass-card">
        <img src="https://img.icons8.com/?size=100&id=423kipnPTZJn&format=png&color=000000" class="icon-img animate-breath" />
        <p class="stat-label">用户数量</p>
        <p class="stat-number gradient-number">{{ animated.userCount }}</p>
      </div>

      <div class="stat-block glass-card">
        <img src="https://img.icons8.com/?size=100&id=SmwWvNEYISh4&format=png&color=000000" class="icon-img animate-breath" />
        <p class="stat-label">商品数量</p>
        <p class="stat-number gradient-number">{{ animated.productCount }}</p>
      </div>
    </div>

    <!-- 第二排：今日新增用户 / 今日订单 / 销售额 -->
    <div class="stats-row mt-6">
      <div class="stat-block glass-card">
        <img src="https://img.icons8.com/?size=100&id=AZazdsitsrgg&format=png&color=000000" class="icon-img animate-breath" />
        <p class="stat-label">今日新增用户</p>
        <p class="stat-number gradient-number">{{ today.newUsers }}</p>
      </div>

      <div class="stat-block glass-card">
        <img src="https://img.icons8.com/?size=100&id=dCS93MYAhxrF&format=png&color=000000" class="icon-img animate-breath" />
        <p class="stat-label">今日订单</p>
        <p class="stat-number gradient-number">{{ today.newOrders }}</p>
      </div>

      <div class="stat-block glass-card">
        <img src="https://img.icons8.com/?size=100&id=wfg9M2fkdTG9&format=png&color=000000" class="icon-img animate-breath" />
        <p class="stat-label">今日销售额</p>
        <p class="stat-number gradient-number">¥ {{ today.revenue }}</p>
      </div>
    </div>

    <!-- 底部折线图 -->
    <div class="glass-card p-6 rounded-2xl shadow-xl mt-6">
      <h2 class="text-xl font-semibold mb-4">近 7 天新增用户</h2>
      <div class="chart-container-top">
        <canvas ref="userChart"></canvas>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import api from '@/services/api'
import Chart from 'chart.js/auto'

const username = localStorage.getItem('admin_username') || '管理员'
const lastLoginTime = localStorage.getItem('last_login_time') || '未知'

const summary = ref({ plantCount: 0, userCount: 0, productCount: 0 })
const animated = ref({ plantCount: 0, userCount: 0, productCount: 0 })
const today = ref({ newUsers: 0, newOrders: 0, revenue: 0 })

const userChart = ref(null)
let chartInstance = null

onMounted(async () => {
  await fetchSummary()
  await fetchToday()
  await fetchNewUsers()
})

// 获取总览
async function fetchSummary() {
  const res = await api.post('/dashboard/summary')
  summary.value = res.data
  animateNumbers()
}

// 获取今日概览
async function fetchToday() {
  const res = await api.get('/dashboard/today-overview')
  today.value = res.data
}

// 近 7 天新增用户
async function fetchNewUsers() {
  const res = await api.get('/users/new?days=7')
  renderChart(res.data.labels, res.data.values)
}

// 数字动画
function animateNumbers() {
  const duration = 1000
  const start = performance.now()

  function update() {
    const now = performance.now()
    const p = Math.min((now - start) / duration, 1)

    animated.value.plantCount = Math.floor(summary.value.plantCount * p)
    animated.value.userCount = Math.floor(summary.value.userCount * p)
    animated.value.productCount = Math.floor(summary.value.productCount * p)

    if (p < 1) requestAnimationFrame(update)
  }
  requestAnimationFrame(update)
}

// 渲染折线图
function renderChart(labels, values) {
  if (chartInstance) chartInstance.destroy()

  chartInstance = new Chart(userChart.value, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: '新增用户',
          data: values,
          borderWidth: 2,
          tension: 0.4,
          fill: true,
        }
      ]
    },
    options: { responsive: true, maintainAspectRatio: false }
  })
}
</script>

<style scoped>
.dashboard { font-family: 'Inter', sans-serif; }

/* 顶部欢迎栏布局 */
.welcome-bar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.welcome-title {
  font-size: 22px;
  font-weight: 700;
  padding: 12px 20px;
}

.last-login {
  padding: 12px 20px;
  font-size: 14px;
  color: #333;
}

/* 玻璃拟态 */
.glass-card {
  backdrop-filter: blur(18px);
  background: rgba(255, 255, 255, 0.35);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 4px 22px rgba(0, 0, 0, 0.1);
}

/* 三横排结构 */
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

/* 大统计卡片 */
.stat-block {
  padding: 26px;
  text-align: center;
  border-radius: 20px;
}

/* 图标 */
.icon-img {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  margin: 0 auto 12px auto;
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
}

/* 图标呼吸动画 */
@keyframes breathSoft {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}
.animate-breath {
  animation: breathSoft 3s ease-in-out infinite;
}

/* 渐变发光数字 */
.gradient-number {
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 10px rgba(79, 70, 229, 0.35);
}

.stat-label {
  font-size: 14px;
  color: #555;
  margin-bottom: 6px;
}

/* 底部折线图 */
.chart-container-top {
  height: 260px;
}
</style>